import { ReactElement, useEffect, useRef } from 'react';
import { useFormatDate } from '@/common/hooks';
import {
    UIDatePresetFilterItem,
    UIDateRangeFilterItem,
    UIFilterGroup,
    DateFilterEnum,
    useUINav
} from '@/ui';
import {
    DeviceFilterItem,
    MiniTaskDetail,
    NoAccessMask,
    TasksList,
    TasksListRef,
    TeamFilterItem,
    UserFilterItem
} from '@/tasks/components';
import { useTaskFilter } from '@/tasks/providers';
import { NavigationType, useLocation, useNavigationType } from 'react-router';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';

const ITEM_SPACING = 16;

let prevScrollValue = 0;

type LocationState = {
    fromTask?: boolean;
};

export function TasksPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { formatDateRange } = useFormatDate();
    const {
        selectedTaskId,
        setSelectedTaskId,
        timeType,
        timeRange,
        timezone,
        setTime
    } = useTaskFilter();

    const taskListRef = useRef<TasksListRef>(null);

    const { hasSubscription, hasLicense } = useCompanyPermissions();

    const navigationType = useNavigationType();
    const location = useLocation();
    const state = location.state as LocationState | null;
    const didComeFromTask = state?.fromTask === true;
    const shouldRememberScroll = useRef(
        didComeFromTask || navigationType === NavigationType.Pop
    );

    useEffect(() => {
        if (shouldRememberScroll.current) {
            setTimeout(() => {
                taskListRef.current?.setScrollPosition(prevScrollValue);
            }, 200);
        } else {
            prevScrollValue = 0;
        }
    }, []);

    const { setParts } = useUINav();
    useEffect(() => {
        setParts([{ text: t('shared.labels.tasks') }]);
    }, [setParts, t]);

    return (
        <div
            style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: ITEM_SPACING,
                paddingLeft: 32,
                paddingTop: ITEM_SPACING,
                paddingRight: 32
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexShrink: 1,
                    gap: ITEM_SPACING
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flex: 2
                    }}
                >
                    <UIFilterGroup>
                        <TeamFilterItem />
                        <UserFilterItem />
                        <DeviceFilterItem />
                    </UIFilterGroup>
                    <UIFilterGroup>
                        <UIDatePresetFilterItem
                            timeType={timeType}
                            popoverPlacement="bottom_right"
                            onDateChange={(_timeType) => {
                                if (_timeType === DateFilterEnum.Custom) {
                                    setTime(DateFilterEnum.Custom, timeRange);
                                } else {
                                    setTime(_timeType);
                                }
                            }}
                        />
                        {timeRange && (
                            <UIDateRangeFilterItem
                                label={formatDateRange(
                                    timeRange.from,
                                    timeRange.to
                                )}
                                from={timeRange.from}
                                to={timeRange.to}
                                timezone={timezone}
                                popoverPlacement="bottom_left"
                                onDateChange={(from, to) => {
                                    setTime(DateFilterEnum.Custom, {
                                        from: from,
                                        to: to
                                    });
                                }}
                            />
                        )}
                    </UIFilterGroup>
                </div>
                <div style={{ display: 'flex', flex: 1 }} />
            </div>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    gap: ITEM_SPACING
                }}
            >
                <div style={{ display: 'flex', flex: 2 }}>
                    <TasksList
                        ref={taskListRef}
                        selected={selectedTaskId}
                        onTaskPress={setSelectedTaskId}
                        onScroll={(value) => {
                            prevScrollValue = value;
                        }}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        paddingBottom: ITEM_SPACING
                    }}
                >
                    <MiniTaskDetail taskId={selectedTaskId} />
                </div>
            </div>
            {!hasSubscription && <NoAccessMask withMessage="no-subscription" />}
            {hasSubscription && !hasLicense && (
                <NoAccessMask withMessage="no-license" />
            )}
        </div>
    );
}
